import React from 'react';
import PropTypes from 'prop-types';

// Utils
import { GroupPropTypes } from 'utils/PropTypesValues';

// Components
import Button from 'sharedComponents/Button';
import MainHeader from 'sharedComponents/MainHeader';
import PageLayout from 'sharedComponents/PageLayout';
import ClientInfiniteScrollList from 'sharedComponents/ClientInfiniteScrollList';
import Preloader from 'sharedComponents/Preloader';
import EmailRecipientItem from '../GroupEmailRecipientsItem';

const GroupRecipientsList = ({
  classes,
  groupId,
  fetchGroupClients,
  fetchGroupDetails,
  groupData,
  groupDataLoading,
  onClose,
  onRecipientsConfirmed,
  initialSelectedRecipientsAmount,
  messageType,
  isEmailRecipientsLimitDisabled,
  newEmailRecipientsLimit,
  isBgUserBrand,
  isUserDCA,
}) => {
  const [selectedClients, setSelectedClients] = React.useState([]);
  const isBgLimitDisabled = isBgUserBrand && isEmailRecipientsLimitDisabled;
  const recalculatedRecipientsLimit =
    isBgLimitDisabled || isUserDCA
      ? selectedClients.length < newEmailRecipientsLimit - initialSelectedRecipientsAmount
      : true;

  React.useEffect(() => {
    fetchGroupDetails({ groupId });
  }, []);

  const fetch = (from, size) => {
    return fetchGroupClients(groupId, { from, size });
  };

  const handleSelectedClients = (client) => {
    if (selectedClients.find(({ profileId }) => profileId === client.profileId)) {
      setSelectedClients(selectedClients.filter(({ profileId }) => profileId !== client.profileId));
    } else if (recalculatedRecipientsLimit) {
      setSelectedClients([...selectedClients, client]);
    } else if (isEmailRecipientsLimitDisabled || isUserDCA) {
      setSelectedClients([...selectedClients, client]);
    }
  };

  if (groupDataLoading) return <Preloader />;

  const renderSubHeaderMessage = () => {
    if ((isBgLimitDisabled || isUserDCA) && selectedClients.length) return `${selectedClients.length} selected`;
    if ((isBgLimitDisabled || isUserDCA) && !selectedClients.length) return 'Select clients to send this message to.';
  };

  return (
    <>
      {/* HEADER */}
      <MainHeader closeIcon="back" onClose={onClose} title={groupData?.name} />
      <div className={classes.subheader}>{renderSubHeaderMessage()}</div>

      {/* BODY */}
      <PageLayout padding="0 0 20px 0">
        <ClientInfiniteScrollList
          displayAlphabet={false}
          fetch={fetch}
          groupId={groupId}
          selectClient={handleSelectedClients}
          selectedClients={selectedClients}
          selectionEnabled
          itemRenderer={(props) => (
            <EmailRecipientItem
              {...props}
              emailRecipientsLimit={recalculatedRecipientsLimit}
              messageType={messageType}
            />
          )}
        />
      </PageLayout>

      {/* FOOTER */}
      <div className={classes.confirmation}>
        <Button disabled={!selectedClients.length} onClick={() => onRecipientsConfirmed(selectedClients)}>
          Continue
        </Button>
      </div>
    </>
  );
};

GroupRecipientsList.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  fetchGroupClients: PropTypes.func.isRequired,
  fetchGroupDetails: PropTypes.func.isRequired,
  groupData: GroupPropTypes,
  groupDataLoading: PropTypes.bool.isRequired,
  groupId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onRecipientsConfirmed: PropTypes.func.isRequired,
  initialSelectedRecipientsAmount: PropTypes.number,
  newEmailRecipientsLimit: PropTypes.number,
};

GroupRecipientsList.defaultProps = {
  classes: {},
  onClose: () => {},
  initialSelectedRecipientsAmount: 0,
  newEmailRecipientsLimit: 1000,
};

export default GroupRecipientsList;
